import React from 'react';
import { useTranslation } from 'react-i18next';
import { PublicLayout } from '../components/layouts';
import { Seo } from '../seo/Seo';
import { DeckHero, DeckSection, useDeckAnimateOnScroll } from '../components/landing/deck';

interface Subsection {
  title: string;
  content: string;
  items?: string[];
  additionalInfo?: string;
}

interface Section {
  title: string;
  content?: string;
  items?: string[];
  subsections?: Record<string, Subsection>;
  contactInfo?: {
    email: string;
    department: string;
    note: string;
  };
}

const sectionKeys = [
  'section1', 'section2', 'section3', 'section4', 'section5',
  'section6', 'section7', 'section8', 'section9', 'section10',
  'section11', 'section12', 'section13', 'section14', 'section15',
  'section16', 'section17',
];

const Terms: React.FC = () => {
  const { t } = useTranslation('terms');
  useDeckAnimateOnScroll();

  const renderSubsection = (subsection: Subsection) => (
    <div className="mt-6 pl-4 border-l border-white/10">
      <h3 className="text-base font-medium text-white/90 mb-2">{subsection.title}</h3>
      <p className="text-sm text-white/60 leading-relaxed">{subsection.content}</p>
      {subsection.items && subsection.items.length > 0 && (
        <ul className="mt-3 space-y-2">
          {subsection.items.map((item, idx) => (
            <li key={idx} className="text-sm text-white/60 leading-relaxed flex gap-3">
              <span className="text-sky-400">—</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
      {subsection.additionalInfo && (
        <p className="text-sm text-white/50 leading-relaxed mt-3 italic">
          {subsection.additionalInfo}
        </p>
      )}
    </div>
  );

  const renderSection = (sectionKey: string) => {
    const section = t(`sections.${sectionKey}`, { returnObjects: true }) as Section;
    if (!section || typeof section === 'string') return null;

    return (
      <section key={sectionKey} className="animate-on-scroll">
        <h2 className="text-2xl font-light text-white mb-4">{section.title}</h2>
        {section.content && (
          <p className="text-base text-white/60 leading-relaxed">{section.content}</p>
        )}
        {section.items && section.items.length > 0 && (
          <ul className="mt-3 space-y-2">
            {section.items.map((item, idx) => (
              <li key={idx} className="text-base text-white/60 leading-relaxed flex gap-3">
                <span className="text-sky-400 mt-1">—</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )}
        {section.subsections &&
          Object.entries(section.subsections).map(([key, subsection]) => (
            <div key={key}>{renderSubsection(subsection)}</div>
          ))}
        {section.contactInfo && (
          <div className="mt-6 pl-4 border-l border-white/10">
            <p className="text-base font-medium text-white/90">
              {section.contactInfo.department}
            </p>
            <a
              href={`mailto:${section.contactInfo.email}`}
              className="text-sky-400 hover:text-sky-300 transition-colors"
            >
              {section.contactInfo.email}
            </a>
            <p className="text-sm text-white/50 mt-2 italic">{section.contactInfo.note}</p>
          </div>
        )}
      </section>
    );
  };

  return (
    <PublicLayout transparentHeader>
      <Seo route="/terms" />

      <DeckHero title={t('title')} subtitle={t('lastUpdated')} />

      <DeckSection>
        <div className="max-w-3xl space-y-12">
          {sectionKeys.map((key) => renderSection(key))}
        </div>
      </DeckSection>
    </PublicLayout>
  );
};

export default Terms;
